import React, { useEffect, useState, useCallback } from "react";
import { listProducts, getFilters } from "../../api/buyerProducts.api";
import { createRFQ } from "../../api/buyerRFQ.api";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useCompare } from "../../context/CompareContext";
import ProductSkeleton from "../../components/ProductSkeleton";

const BuyerProducts = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();
  const { items: compareList, addToCompare, removeFromCompare } = useCompare();
  const [page, setPage] = useState(1);
  const perPage = 9;
  const [filters, setFilters] = useState({
    categories: [],
    families: [],
    locations: [],
    specTypes: [],
    specValues: []
  });

  const [query, setQuery] = useState(routerLocation.state?.search || "");
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const [category, setCategory] = useState("");
  const [family, setFamily] = useState("");
  const [location, setLocation] = useState("");
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState("relevance");

  // MOBILE STATE DETECTION
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 991);
  const [showFilters, setShowFilters] = useState(!isMobile);

  const [selectedSpecs, setSelectedSpecs] = useState([]);
  const [specSearch, setSpecSearch] = useState("");

  const [view, setView] = useState("grid");

  // Filter section expanded/collapsed state
  const [expandedSections, setExpandedSections] = useState({
    categories: false,
    families: false,
    locations: false,
    specifications: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  /* --- STYLING CONSTANTS --- */
  const colors = {
    primary: "#112347",
    accent: "#ffca44",
    success: "#059669",
    lightBg: "#f8f9fa",
    border: "#e5e7eb",
    textMuted: "#6b7280"
  };

  const labelStyle = {
    fontSize: "14px",
    fontWeight: "700",
    color: colors.primary,
    marginBottom: "12px",
    marginTop: "20px",
    display: "block",
    cursor: "pointer"
  };

  /* FETCH PRODUCTS */
  const fetchData = useCallback(() => {
    setIsLoading(true);
    listProducts({
      skip: (page - 1) * perPage,
      per_page: perPage,
      match: debouncedQuery,
      category,
      family,
      location,
      specFilters: selectedSpecs
    }).then(res => {
      let products = res.data.data[0].data;

      if (sort === "rating") {
        products.sort((a, b) => (b.rating || 0) - (a.rating || 0));
      }
      if (sort === "reviews") {
        products.sort((a, b) => (b.review_count || 0) - (a.review_count || 0));
      }
      if (sort === "newest") {
        products.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      }

      // Simulate 2 second loading delay
      setTimeout(() => {
        setData(products);
        setTotal(res.data.data[0].count);
        setIsLoading(false);
      }, 2000);
    }).catch(() => {
      setIsLoading(false);
    });
  }, [page, debouncedQuery, category, family, location, selectedSpecs, sort]);

  // RESIZE LISTENER
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 991;
      setIsMobile(mobile);
      if (!mobile) setShowFilters(true);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const handler = (e) => {
      setQuery(e.detail);
      setPage(1);
    };
    window.addEventListener("globalSearch", handler);
    return () => window.removeEventListener("globalSearch", handler);
  }, []);

  useEffect(() => {
    getFilters(category, family).then(res => {
      setFilters(res.data.data);
    });
  }, [category, family]);

  useEffect(() => {
    if (!category || !family) {
      setFilters(prev => ({
        ...prev,
        specTypes: [],
        specValues: []
      }));
    }
  }, [category, family]);

  useEffect(() => {
    if (routerLocation.state?.search !== undefined) {
      setQuery(routerLocation.state.search);
    }
  }, [routerLocation.state]);

  useEffect(() => {
    const delay = setTimeout(fetchData, 400);
    return () => clearTimeout(delay);
  }, [debouncedQuery, fetchData]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, category, family, location, selectedSpecs]);

  /* SPEC FUNCTIONS */
  const addSpec = (spec) => {
    if (!selectedSpecs.find(s => s.spec_id === spec.id)) {
      setSelectedSpecs([...selectedSpecs, { spec_id: spec.id, label: spec.spec_label, values: [] }]);
    }
  };

  const addValue = (spec_id, value) => {
    setSelectedSpecs(prev => prev.map(s => {
      if (s.spec_id === spec_id && !s.values.includes(value)) {
        return { ...s, values: [...s.values, value] };
      }
      return s;
    }));
  };

  const removeSpec = (spec_id) => {
    setSelectedSpecs(prev => prev.filter(s => s.spec_id !== spec_id));
  };

  /* RFQ & SAVE */
  const handleRFQ = async (row) => {
    if (!user) {
      window.dispatchEvent(new Event("openLogin"));
      return;
    }
    try {
      const res = await createRFQ({
        seller_product_id: row.listed_product_id,
        quantity: 1
      });

      if (res.data.isOk) {
        toast.success("RFQ Sent Successfully");
      } else {
        toast.error(res.data.message);
      }

    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    }
  };

  return (
    <div className="container-fluid py-4" style={{ backgroundColor: "rgb(203, 220, 237)", minHeight: "100vh" }}>

      {/* HEADER SECTION */}
      <div className="px-3 mb-4">
        <h2 className="fw-bold m-0" style={{ color: colors.primary, letterSpacing: "-0.5px", fontSize: "clamp(24px, 4vw, 32px)" }}>Industrial Equipment Catalog</h2>
        <p className="text-muted small">Browse different verified products</p>
      </div>

      {/* SEARCH & ACTION BAR */}
      <div className="d-flex justify-content-between align-items-center bg-white p-2 p-md-3 rounded-3 shadow-sm mb-4 mx-3 border gap-2 gap-md-3" style={{ flexWrap: "nowrap", overflowX: "auto" }}>
        <div className="d-flex gap-2 flex-shrink-0">
          <button
            className="btn d-flex align-items-center gap-2 px-2 px-md-3 fw-bold text-nowrap"
            onClick={() => setShowFilters(!showFilters)}
            style={{
              backgroundColor: "#f8f9fa",
              border: "1px solid #dee2e6",
              color: "#000",
              borderRadius: "8px",
              padding: "6px 12px"
            }}
          >
            <span style={{ fontSize: "18px", lineHeight: "1" }}>≡</span>

            <span
              className="d-none d-sm-inline"
              style={{
                fontSize: "16px",
                fontWeight: "600"
              }}
            >
              Filters
            </span>
          </button>
        </div>

        <div className="d-flex gap-2 gap-md-3 align-items-center flex-shrink-0">
          <button
            className="btn fw-bold d-flex align-items-center gap-1 gap-md-2 px-2 px-md-3 text-nowrap"
            style={{
              backgroundColor: colors.accent,
              border: "none",
              borderRadius: "8px",
              fontSize: "clamp(12px, 2vw, 14px)",
              minWidth: "100px",
              justifyContent: "center",
              padding: "4px 12px"
            }}
            onClick={() => navigate("/compare")}
          >
            <>
              <span>Compare</span>
              ({compareList.length})
            </>
          </button>

          <select
            className="form-select border-0 bg-light fw-bold text-muted"
            style={{ fontSize: "clamp(12px, 2vw, 14px)", borderRadius: "8px", minWidth: "auto", maxWidth: "140px" }}
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            <option value="relevance">Relevance</option>
            <option value="rating">Highest Rated</option>
            <option value="reviews">Most Reviews</option>
            <option value="newest">Newest First</option>
          </select>

          {/* Hidden on mobile */}
          <div className="btn-group border rounded-2 p-1 bg-light d-none d-md-flex flex-shrink-0">
            <button
              className={`btn btn-sm border-0 px-2 ${view === "grid" ? "bg-white shadow-sm" : "text-muted"}`}
              onClick={() => setView("grid")}
            >
              ▦
            </button>
            <button
              className={`btn btn-sm border-0 px-2 ${view === "list" ? "bg-white shadow-sm" : "text-muted"}`}
              onClick={() => setView("list")}
            >
              ≣
            </button>
          </div>
        </div>
      </div>

      <div className="row px-3 g-4">

        {/* MOBILE BACKDROP */}
        {isMobile && showFilters && (
          <div
            style={{
              position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1040
            }}
            onClick={() => setShowFilters(false)}
          />
        )}

        {/* SIDEBAR FILTERS - Updated display logic */}
        <div
          className={isMobile ? "" : "col-lg-3 col-md-4"}
          style={isMobile ? {
            position: "fixed",
            top: 0,
            left: showFilters ? 0 : "-100%",
            width: "85%",
            maxWidth: "320px",
            height: "100vh",
            backgroundColor: "#fff",
            zIndex: 1050,
            transition: "left 0.3s ease",
            overflowY: "auto",
            padding: "20px",
            boxShadow: "2px 0 10px rgba(0,0,0,0.1)"
          } : {
            display: showFilters ? "block" : "none" // Hides column completely on desktop when toggled
          }}
        >
          <div className={isMobile ? "" : "bg-white border rounded-3 p-4 shadow-sm sticky-top"} style={isMobile ? {} : { top: "20px", maxHeight: "85vh", overflowY: "auto" }}>

            {isMobile && (
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="fw-bold m-0" style={{ color: colors.primary }}>Filters</h5>
                <button className="btn btn-link text-dark p-0 fs-4 text-decoration-none" onClick={() => setShowFilters(false)}>✕</button>
              </div>
            )}

            <h6 style={labelStyle} className="d-flex justify-content-between align-items-center cursor-pointer" onClick={() => toggleSection('categories')}>
              <span >Categories</span>
              <span style={{ fontSize: "18px", fontWeight: "bold" }}>{expandedSections.categories ? "−" : "+"}</span>
            </h6>
            {expandedSections.categories && (
              <>
                {filters.categories.map(c => (
                  <div key={c.category_name} className="form-check mb-2">
                    <input className="form-check-input" type="checkbox" checked={category === c.category_name} onChange={() => setCategory(category === c.category_name ? "" : c.category_name)} id={`cat-${c.category_name}`} />
                    <label className="form-check-label small text-muted fw-bold mt-1" htmlFor={`cat-${c.category_name}`}>{c.category_name}</label>
                  </div>
                ))}
              </>
            )}
            {!expandedSections.categories && filters.categories.length === 0 && null}

            <hr className="my-3 opacity-10" />

            <h6 style={labelStyle} className="d-flex justify-content-between align-items-center cursor-pointer" onClick={() => toggleSection('families')}>
              <span>Family Group</span>
              <span style={{ fontSize: "18px", fontWeight: "bold" }}>{expandedSections.families ? "−" : "+"}</span>
            </h6>
            {expandedSections.families && (
              <>
                {filters.families.map(f => (
                  <div key={f.family_name} className="form-check mb-2">
                    <input className="form-check-input" type="checkbox" checked={family === f.family_name} onChange={() => setFamily(family === f.family_name ? "" : f.family_name)} id={`fam-${f.family_name}`} />
                    <label className="form-check-label small text-muted fw-bold mt-1" htmlFor={`fam-${f.family_name}`}>{f.family_name}</label>
                  </div>
                ))}
              </>
            )}

            <hr className="my-3 opacity-10" />

            <h6 style={labelStyle} className="d-flex justify-content-between align-items-center cursor-pointer" onClick={() => toggleSection('locations')}>
              <span>Location</span>
              <span style={{ fontSize: "18px", fontWeight: "bold" }}>{expandedSections.locations ? "−" : "+"}</span>
            </h6>
            {expandedSections.locations && (
              <>
                {filters.locations.map(l => (
                  <div key={l.city} className="form-check mb-2">
                    <input className="form-check-input" type="checkbox" checked={location === l.city} onChange={() => setLocation(location === l.city ? "" : l.city)} id={`loc-${l.city}`} />
                    <label className="form-check-label small text-muted fw-bold mt-1" htmlFor={`loc-${l.city}`}>{l.city}</label>
                  </div>
                ))}
              </>
            )}

            <hr className="my-3 opacity-10" />

            <h6 style={labelStyle} className="d-flex justify-content-between align-items-center cursor-pointer" onClick={() => toggleSection('specifications')}>
              <span>Specifications</span>
              <span style={{ fontSize: "18px", fontWeight: "bold" }}>{expandedSections.specifications ? "−" : "+"}</span>
            </h6>
            {expandedSections.specifications && (
              <div className="mt-2">
                <div className="input-group input-group-sm mb-3">
                  <span className="input-group-text bg-white border-end-0 text-muted">🔍</span>
                  <input className="form-control border-start-0 ps-0" placeholder="Search spec type..." value={specSearch} onChange={(e) => setSpecSearch(e.target.value)} />
                </div>

                <div style={{ maxHeight: 150, overflowY: "auto" }} className="mb-3 border rounded p-2">
                  {!category || !family ? (
                    <div className="text-muted small text-center py-3">
                      Select Category and Family first
                    </div>
                  ) : (
                    filters.specTypes
                      ?.filter(s =>
                        s.spec_label.toLowerCase().includes(specSearch.toLowerCase())
                      )
                      .map(s => (
                        <div
                          key={s.id}
                          className="p-2 border-bottom small fw-bold text-primary cursor-pointer"
                          onClick={() => addSpec(s)}
                          style={{ transition: "0.2s" }}
                        >
                          + {s.spec_label}
                        </div>
                      ))
                  )}
                </div>

                {selectedSpecs.map(spec => (
                  <div key={spec.spec_id} className="mb-4 bg-light p-2 rounded border">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <b className="small text-uppercase" style={{ color: colors.primary, fontSize: '11px' }}>{spec.label}</b>
                      <span className="text-danger cursor-pointer" onClick={() => removeSpec(spec.spec_id)}>✕</span>
                    </div>
                    {filters.specValues
                      ?.filter(v => v.spec_id === spec.spec_id)
                      .flatMap(v =>
                        v.spec_value.split(",").map(val => ({
                          ...v,
                          spec_value: val.trim()
                        }))
                      )
                      .map((v, i) => (
                        <div key={i} className="form-check small mb-1">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            onChange={() => addValue(spec.spec_id, v.spec_value)}
                          />
                          <label className="form-check-label text-muted">
                            {v.spec_value}
                          </label>
                        </div>
                      ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* PRODUCTS AREA - Remains visible and expands to col-12 when sidebar is hidden */}
        <div className={(!isMobile && showFilters) ? "col-lg-9 col-md-8" : "col-12"}>
          <div className="row g-4">
            {isLoading ? (
              // Show skeleton screens while loading
              Array.from({ length: perPage }).map((_, i) => (
                <ProductSkeleton
                  key={`skeleton-${i}`}
                  view={view}
                  isMobile={isMobile}
                  showFilters={showFilters}
                />
              ))
            ) : (
              // Show actual products when loaded
              data.map((item, i) => (
              <div
                className={
                  view === "grid"
                    ? (!isMobile && showFilters ? "col-sm-6 col-lg-4" : "col-sm-6 col-lg-3")
                    : "col-12"
                }
                key={i}
              >
                <div
                  className={`card border-0 shadow-sm rounded-4 overflow-hidden position-relative h-100 ${view === "list" && !isMobile ? "flex-row" : ""}`}
                  style={{ transition: 'transform 0.2s ease', border: `1px solid ${colors.border}` }}
                >

                  {/* Category Tag Overlay */}
                  <span className="position-absolute p-2 px-3 m-3 rounded-pill text-white fw-bold" style={{ backgroundColor: colors.primary, fontSize: '10px', zIndex: 2, top: 0, left: 0 }}>
                    {item.category_name}
                  </span>

                  {/* Compare Action Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const exists = compareList.find(
                        p => p.listed_product_id === item.listed_product_id
                      );
                      if (exists) {
                        removeFromCompare(item.listed_product_id);
                      } else {
                        addToCompare(item);
                      }
                    }}
                    className="position-absolute m-3 btn shadow-sm rounded-circle d-flex align-items-center justify-content-center border-0"
                    style={{
                      width: '34px',
                      height: '34px',
                      zIndex: 2,
                      top: 0,
                      right: 0,
                      backgroundColor: compareList.find(p => p.listed_product_id === item.listed_product_id) ? colors.accent : "#fff"
                    }}
                  >
                    <span style={{ fontSize: '18px', color: colors.primary, fontWeight: 'bold' }}>
                      {compareList.find(p => p.listed_product_id === item.listed_product_id) ? "✓" : "+"}
                    </span>
                  </button>

                  <div
                    onClick={() => navigate(`/product/${item.listed_product_id}`)}
                    style={{
                      width: view === "list" && !isMobile ? "clamp(200px, 30%, 280px)" : "100%",
                      height: "auto",
                      aspectRatio: view === "list" && !isMobile ? "auto" : "3 / 2",
                      backgroundColor: item.main_image ? "#f8f6f2" : "#e15449",
                      borderRight: view === "list" && !isMobile ? `1px solid ${colors.border}` : "none",
                      borderBottom: view !== "list" || isMobile ? `1px solid ${colors.border}` : "none",
                      cursor: "pointer",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden"
                    }}
                  >
                    {item.main_image ? (
                      <img
                        src={`https://aaeternitas.com/uploads/products/${item.main_image}`}
                        alt={item.product_name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          imageRendering: "pixelated"
                        }}
                      />
                    ) : (
                      <span style={{ color: "#fff", fontSize: "14px", fontWeight: "600" }}>
                        No Image
                      </span>
                    )}
                  </div>

                  <div className="card-body p-4 d-flex flex-column" onClick={() => navigate(`/product/${item.listed_product_id}`)} style={{ cursor: 'pointer' }}>
                    <h5 className="fw-bold mb-2" style={{ color: colors.primary, fontSize: "clamp(16px, 1.5vw, 18px)" }}>{item.product_name}</h5>

                    <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
                      <span className="fw-bold small" style={{ color: colors.primary }}>{item.business_name}</span>
                      <span style={{
                        backgroundColor: "#ecfdf5",
                        color: colors.success,
                        fontSize: '11px',
                        border: `1px solid ${colors.success}`,
                        borderRadius: '20px',
                        padding: '1px 10px',
                        fontWeight: '600'
                      }}>
                        ✓ Verified
                      </span>
                    </div>

                    <div className="small text-muted mb-3 d-flex flex-wrap align-items-center gap-1">
                      <span style={{ fontSize: "14px" }}>📍</span> {item.city}, {item.state_code || "IN"} | <span className="ms-1">🏭 Manufacturer</span>
                    </div>

                    {/* DYNAMIC SPECS MAPPING */}
                    <div className="row g-2 mb-3 mt-1 border-top pt-3">
                      <div className="col-12 col-sm-6">
                        <div className="small text-muted text-truncate" style={{ fontSize: "11px" }}>Load Capacity: <span className="fw-bold text-dark">{item.load_capacity || "N/A"}</span></div>
                        <div className="small text-muted text-truncate" style={{ fontSize: "11px" }}>Max Lift: <span className="fw-bold text-dark">{item.max_lift || "N/A"}</span></div>
                        <div className="small text-muted text-truncate" style={{ fontSize: "11px" }}>Power: <span className="fw-bold text-dark">{item.power_source || "N/A"}</span></div>
                      </div>
                      <div className="col-12 col-sm-6">
                        <div className="small text-muted text-truncate" style={{ fontSize: "11px" }}>Wheel: <span className="fw-bold text-dark">{item.wheel_type || "N/A"}</span></div>
                        <div className="small text-muted text-truncate" style={{ fontSize: "11px" }}>WOF: <span className="fw-bold text-dark">{item.width_over_fork || "N/A"}</span></div>
                      </div>
                    </div>

                    <div className="mt-auto pt-3 d-flex flex-wrap justify-content-between align-items-center border-top gap-2">
                      <div className="d-flex align-items-center gap-1">
                        <span style={{ color: colors.accent, fontSize: "18px" }}>★</span>
                        <span className="fw-bold" style={{ fontSize: "15px" }}>{Number(item.rating || 4.8).toFixed(1)}</span>
                        <span className="text-muted small">({item.review_count})</span>
                      </div>
                      <button
                        className="btn btn-dark btn-sm fw-bold px-4 py-2 text-white"
                        style={{ backgroundColor: colors.primary, borderRadius: "8px", border: "none" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRFQ(item);
                        }}
                      >
                        RFQ
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
            )}
          </div>

          {/* PAGINATION */}
          <div className="d-flex flex-wrap justify-content-center gap-2 gap-md-3 mt-5 mb-5">
            {Array.from({ length: Math.ceil(total / perPage) }, (_, i) => (
              <button
                key={i}
                className={`btn rounded-circle fw-bold transition-all p-0`}
                style={{
                  width: '40px',
                  height: '40px',
                  minWidth: '40px',
                  minHeight: '40px',
                  flexShrink: 0,
                  backgroundColor: page === i + 1 ? colors.primary : '#fff',
                  color: page === i + 1 ? '#fff' : colors.primary,
                  border: `1px solid ${page === i + 1 ? colors.primary : colors.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: page === i + 1 ? "0 4px 6px rgba(17, 35, 71, 0.2)" : "0 1px 3px rgba(0,0,0,0.05)",
                  lineHeight: 1
                }}
                onClick={() => setPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyerProducts;